import React, {useContext, useMemo, useState} from 'react';
import {Button} from 'reactstrap';
import {Download, Eye, FileText, Image as ImageIcon, Trash2} from 'react-feather';
import AuthorizationContext from '../../../../_helper/Authorization';
import type {AuthorizationContextType} from '../../../../_helper/Authorization/AuthorizationTypes';
import MultimediaService from '../Services/Multimedia.service';
import type {Multimedia, MultimediaPreviewProps} from '../Types/MultimediaTypes';

const MultimediaPreview: React.FC<MultimediaPreviewProps> = ({
    multimedia,
    onDelete,
    onDownload,
    onView,
    showActions = true,
    hideDelete = false,
    hideDownload = false,
    hideView = false,
}) => {
    const [processingAction, setProcessingAction] = useState<'view' | 'download' | 'delete' | null>(null);
    const authContext = useContext(AuthorizationContext) as AuthorizationContextType;
    const {errorHandler} = authContext || {};

    const normalizedMultimedia = useMemo(
        () => MultimediaService.normalizeMultimediaRelation(multimedia),
        [multimedia],
    );

    const fileName = useMemo(() => MultimediaService.resolveFileName(normalizedMultimedia), [normalizedMultimedia]);
    const previewUrl = useMemo(
        () => MultimediaService.resolveMultimediaUrl(normalizedMultimedia),
        [normalizedMultimedia],
    );

    const mimeType = normalizedMultimedia?.mime_type?.toLowerCase() || '';
    const isImage = mimeType.startsWith('image/');
    const isPdf = mimeType.includes('pdf');

    const getFormattedSize = (size?: number) => {
        if (typeof size !== 'number') return null;
        if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
        if (size >= 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${size} B`;
    };

    const handleAction = async (type: 'view' | 'download' | 'delete', callback: () => Promise<void> | void) => {
        try {
            setProcessingAction(type);
            await callback();
        } catch (exception: any) {
            errorHandler(exception);
        } finally {
            setProcessingAction(null);
        }
    };

    const defaultView = async (item: Multimedia) => {
        await MultimediaService.viewMultimedia(item);
    };

    const defaultDownload = async (item: Multimedia) => {
        await MultimediaService.downloadMultimedia(item);
    };

    if (!normalizedMultimedia) return null;

    return (
        <div className='border rounded p-3 d-flex flex-column gap-3'>
            <div className='d-flex align-items-start gap-3 flex-wrap'>
                <div
                    className='border rounded d-flex align-items-center justify-content-center overflow-hidden bg-light'
                    style={{width: 96, height: 96, flexShrink: 0}}
                >
                    {isImage && previewUrl ? (
                        <img
                            src={previewUrl}
                            alt={fileName}
                            style={{width: '100%', height: '100%', objectFit: 'cover'}}
                        />
                    ) : (
                        <div className='d-flex flex-column align-items-center justify-content-center text-muted'>
                            {isPdf ? <FileText size={28}/> : isImage ? <ImageIcon size={28}/> : <FileText size={28}/>}
                            <small className='mt-1'>{normalizedMultimedia.extension || mimeType || 'file'}</small>
                        </div>
                    )}
                </div>

                <div className='flex-grow-1'>
                    <div className='fw-bold'>{fileName}</div>
                    <div className='text-muted small'>{normalizedMultimedia.mime_type || normalizedMultimedia.extension || 'Unknown type'}</div>
                    {typeof normalizedMultimedia.size === 'number' ? <div className='text-muted small'>{getFormattedSize(normalizedMultimedia.size)}</div> : null}
                </div>
            </div>

            {showActions && (
                <div className='d-flex align-items-center gap-2 flex-wrap'>
                    {!hideView && (
                        <Button
                            color='light'
                            size='sm'
                            disabled={processingAction !== null}
                            onClick={() => handleAction('view', () => onView ? onView(normalizedMultimedia) : defaultView(normalizedMultimedia))}
                        >
                            <Eye size={14} className='me-1'/>
                            View
                        </Button>
                    )}
                    {!hideDownload && (
                        <Button
                            color='light'
                            size='sm'
                            disabled={processingAction !== null}
                            onClick={() => handleAction('download', () => onDownload ? onDownload(normalizedMultimedia) : defaultDownload(normalizedMultimedia))}
                        >
                            <Download size={14} className='me-1'/>
                            Download
                        </Button>
                    )}
                    {!hideDelete && onDelete && (
                        <Button
                            color='danger'
                            size='sm'
                            disabled={processingAction !== null}
                            onClick={() => handleAction('delete', () => onDelete(normalizedMultimedia))}
                        >
                            <Trash2 size={14} className='me-1'/>
                            Delete
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};

export default MultimediaPreview;



